import { Component } from '@angular/core';
import { ShipmentTripLists } from '../models/shipment-trip-lists.model';
import { NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ColDef
} from 'ag-grid-community';
import { TripDetailsComponent } from './trip-details/trip-details.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BlindUploadModalComponent } from './blind-upload-modal/blind-upload-modal.component';
import { ShipmentListsModalComponent } from '../shipment-lists/shipment-lists-modal/shipment-lists-modal.component';

@Component({
  selector: 'app-trip-lists',
  templateUrl: './trip-lists.component.html',
  styleUrls: ['./trip-lists.component.scss']
})
export class TripListsComponent {
  public detailCellRenderer: any = TripDetailsComponent;

  defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    cellClass: 'cell-wrap-text'
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private dialog: MatDialog,
    private zone: NgZone
  ) {}

  columnDefs: ColDef[] = [
    { field: 'trip_list_uuid', headerName: 'UUID', hide: true },
    {
      field: 'asn_number',
      headerName: 'ASN No.',
      cellRenderer: 'agGroupCellRenderer',
      filter: 'agTextColumnFilter',
      width: 200,
      cellStyle: { 'font-weight': 'bold' }
    },
    {
      field: 'trip_number',
      headerName: 'Trip No.',
      filter: 'agNumberColumnFilter',
      width: 200,
      cellStyle: { 'font-weight': 'bold' },
      cellRenderer: (params: any) => {
        const container = document.createElement('span');
        container.textContent = params.value;

        if (params.data?.blind_scan === true && !params.data?.metadata_uploaded) {
          const icon = document.createElement('i');
          icon.classList.add('fas', 'fa-eye-slash');
          icon.style.color = '#000000';
          icon.style.marginLeft = '6px';
          icon.title = 'Blind trip - requires metadata upload';
          container.appendChild(icon);
        }

        return container;
      }
    },
    { field: 'date', headerName: 'Date' },
    { field: 'week_no', headerName: 'Week No.' },
    { field: 'external_bill_of_lading', headerName: 'Bill Of Lading No.' },
    { field: 'act_trail_number', headerName: 'ACT Trailer No.', width: 170 },
    { field: 'trailer_id', headerName: 'Trailer ID' },
    { field: 'archive', headerName: 'Archive' },
    { field: 'total_cartons', headerName: 'Total Cartons' },
    { field: 'manifest_carton_count', headerName: 'Manifest Carton Count' },
    { field: 'bol_carton_count', headerName: 'BOL Carton Count' },
    { field: 'reported_weight', headerName: 'Reported Weight' },
    { field: 'created', headerName: 'Date Created' },
    {
      headerName: 'Action',
      cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');
        icon.classList.add('fas', 'fa-trash');
        button.appendChild(icon);

        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.deleteTrip(params);
          });
        });

        return button;
      },
      editable: false,
      width: 100
    }
  ];

  private gridApi!: GridApi;

  onGridReady(params: any) {
    this.gridApi = params.api;
    params.api.setGridOption(
      'serverSideDatasource',
      this.getServerSideDatasource()
    );
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        this.getShipments(
          params.request.startRow,
          params.request.endRow,
          params.request.filterModel,
          params.request.sortModel
        ).subscribe(
          response => {
            if (response) {
              params.success({
                rowData: response.rowData,
                rowCount: response.rowCount
              });
            } else {
              params.fail();
            }
          },
          error => {
            console.log(error);
            params.fail();
          }
        );
      }
    };
  }

  getContextMenuItems = (params: any) => {
    const items: any[] = ['copy', 'copyWithHeaders', 'export', 'separator'];

    if (params.node?.data?.blind_scan) {
      items.unshift({
        name: 'Upload Blind Data',
        icon: '<i class="fas fa-upload"></i>',
        action: () => {
          this.zone.run(() =>
            this.openBlindUploadModal(params.node.data)
          );
        }
      });
    }

    return items;
  };

  openBlindUploadModal(rowData: any) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '900px';     
    dialogConfig.maxWidth = '90vw';   
    dialogConfig.height = '80vh';     
    dialogConfig.maxHeight = '90vh';  
    dialogConfig.data = {
      modalTitle: `Upload Data for Trip ${rowData.trip_number}`,
      trip: rowData
    };

    const dialogRef = this.dialog.open(BlindUploadModalComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Blind upload payload:', result);
        // TODO: send payload to backend
      }
    });
  }

  getShipments(
    startRow: number = 0,
    endRow: number = 0,
    filterModel: any = {},
    sortModel: any = []
  ) {
    const urlApi = '/api/shipment_lists/trip_lists.php';

    const filters = Object.entries(filterModel).map(
      ([column, filter]: [string, any]) => {
        let operator = '';
        let value: any = [];

        if (filter.filterType === 'text') {
          operator = filter.type;
          value = filter.filter;
        } else if (filter.filterType === 'set') {
          operator = 'equals';
          value = filter.values;
        } else if (filter.filterType === 'date') {
          if (filter.type == 'inRange') {
            operator = filter.type;
            value = [filter.dateFrom, filter.dateTo];
          } else {
            operator = 'equals';
            value = filter.dateFrom;
          }
        } else if (filter.filterType === 'number') {
          if (filter.type == 'inRange') {
            operator = filter.type;
            value = [filter.filter, filter.filterTo];
          } else {
            operator = 'equals';
            value = filter.filter;
          }
        }

        return { column, operator, value };
      }
    );

    const params: any = {
      startRow: startRow,
      endRow: endRow,
      sortModel: JSON.stringify(sortModel),
      filters: JSON.stringify(filters)
    };

    return this.http.get<any>(urlApi, { params }).pipe(
      map(response => {
        const rowData = response.data.map((tripListsData: any) => {
          const tripLists = new ShipmentTripLists();
          Object.assign(tripLists, tripListsData);
          return tripLists;
        });

        return { rowData, rowCount: response.totalRows };
      }),
      catchError(error => {
        console.log(error);
        return of(null);
      }),
      filter(response => response !== null)
    );
  }

  refresh(params: any): boolean {
    return false;
  }

  storeReports() {
    this.router.navigate(['/store_reports']);
  }

  exceptionReports() {
    this.router.navigate(['/exception_reports']);
  }

  deliveryReports() {
    this.router.navigate(['/delivery_reports']);
  }

  deleteTrip(params: any) {
   const dialogConfig = new MatDialogConfig();    
    const trip_list_uuid = params.data.trip_list_uuid;
    const asn_number = params.data.asn_number;
    const trip_number = params.data.trip_number;

    console.log('Delete trip requested:', {
      trip_list_uuid,
      asn_number,
      trip_number
    });
    
    dialogConfig.data = {
      data: {
        modalTitle: 'Delete Trip List Confirmation',
        modalMessage: {trip_list_uuid: trip_list_uuid, asn_number: asn_number, trip_number: trip_number},
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);

  }

  uploadAsnFile() {
   const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '530px'; 
    dialogConfig.height = '950px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Upload ASN File',
        modalMessage: '',
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }


  uploadYoshinoFile() {
   const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '530px'; 
    dialogConfig.height = '200px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Upload Yoshino File',
        modalMessage: '',
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }

}
